import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Check,
  Copy,
  Link as LinkIcon,
  MapPin,
  Pencil,
  PlusCircle,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePmProperty } from '@/hooks/usePmProperty';
import { getPropertyTypeLabel } from '@/lib/property-type';
import { extractApiErrorMessage } from '@/lib/api-error';
import type { PropertyGate, Unit } from '@/types';
import {
  getPropertyConfig,
  updatePropertyConfig,
  getPmGates,
  createGate,
  updateGate,
  deleteGate,
  getBranding,
  updateBranding,
  uploadBrandingLogo,
  getPropertyModules,
  getUnits,
  getZonesForUnits,
  createUnit,
  updateUnit,
  deleteUnit,
} from '@/services/api.client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/pm/ConfirmDialog';

// ── Details tab ───────────────────────────────────────────────────────────────

// Property type is intentionally NOT in this schema — ADR 008 §Consequences
// states "type changes are not self-service". The PM/Building-Manager sees
// the current type as a read-only label; reclassification goes through Super
// Admin / Org Admin.
const detailsSchema = z.object({
  name: z.string().min(1),
  address: z.string().min(1),
  registrationMode: z.enum(['manager_initiated', 'self_registration']),
});
type DetailsValues = z.infer<typeof detailsSchema>;

function RegistrationLinkCallout({ subdomain }: { subdomain: string }) {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = `${origin}/register/${subdomain}`;

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-md border border-[var(--status-blue)]/30 bg-[var(--status-blue)]/5 p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        <LinkIcon className="size-4 text-[var(--status-blue-text)]" />
        Self-Registration Link
      </div>
      <p className="text-xs text-muted-foreground">
        Share this link with residents so they can register themselves. You will be notified when
        someone registers and can approve or reject them from the Users page.
      </p>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={link}
          className="flex-1 text-xs bg-background font-mono"
          onClick={(e) => (e.target as HTMLInputElement).select()}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => void copy()}
        >
          {copied ? (
            <Check className="size-4 text-[var(--status-green-text)]" />
          ) : (
            <Copy className="size-4" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

function DetailsTab({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['pm', 'property-config', propertyId],
    queryFn: () => getPropertyConfig(propertyId),
    enabled: !!propertyId,
  });

  const mutation = useMutation({
    mutationFn: (values: DetailsValues) => updatePropertyConfig(propertyId, values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'property-config', propertyId] });
      toast.success('Property details saved');
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<DetailsValues>({
    resolver: zodResolver(detailsSchema),
  });

  useEffect(() => {
    if (query.data) reset(query.data as DetailsValues);
  }, [query.data, reset]);

  if (query.isLoading) return <Skeleton className="h-64 rounded-lg" />;

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4 max-w-lg">
      <div className="space-y-1.5">
        <Label htmlFor="prop-name">Property Name</Label>
        <Input id="prop-name" {...register('name')} />
        {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="prop-address">Address</Label>
        <Input id="prop-address" {...register('address')} />
        {errors.address && <p className="text-sm text-destructive">{errors.address.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="prop-type">Property Type</Label>
        <div
          id="prop-type"
          className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground"
          data-testid="property-type-readonly"
        >
          {getPropertyTypeLabel(query.data?.propertyType)}
        </div>
        <p className="text-xs text-muted-foreground">
          Property type changes are made by your Org Admin — they affect which screens and modules
          apply across the property.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="prop-reg-mode">Registration Mode</Label>
        <Select
          {...(query.data?.registrationMode && { defaultValue: query.data.registrationMode })}
          onValueChange={(v) =>
            setValue('registrationMode', v as DetailsValues['registrationMode'])
          }
        >
          <SelectTrigger id="prop-reg-mode">
            <SelectValue placeholder="Select a mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manager_initiated">Invite only (manager-initiated)</SelectItem>
            <SelectItem value="self_registration">Allow resident self-registration</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {watch('registrationMode') === 'self_registration' && query.data?.subdomain && (
        <RegistrationLinkCallout subdomain={query.data.subdomain} />
      )}

      {mutation.isError && (
        <p className="text-sm text-destructive">Save failed. Please try again.</p>
      )}
      <Button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? 'Saving…' : 'Save Changes'}
      </Button>
    </form>
  );
}

// ── Gates tab ─────────────────────────────────────────────────────────────────

const gateSchema = z.object({
  name: z.string().min(1, 'Gate name is required'),
  latitude: z
    .number({ error: 'Latitude is required' })
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90'),
  longitude: z
    .number({ error: 'Longitude is required' })
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180'),
  entryInstructions: z.string().max(500, 'Max 500 characters').optional(),
});
type GateValues = z.infer<typeof gateSchema>;

function GateFormDialog({
  propertyId,
  open,
  onOpenChange,
  gate,
}: {
  propertyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gate?: PropertyGate | null;
}) {
  const qc = useQueryClient();
  const isEdit = !!gate;
  const [geoError, setGeoError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<GateValues>({
    resolver: zodResolver(gateSchema),
  });

  useEffect(() => {
    if (!open) return;
    setGeoError(null);
    reset(
      gate
        ? {
            name: gate.name,
            latitude: gate.latitude ?? (undefined as unknown as number),
            longitude: gate.longitude ?? (undefined as unknown as number),
            entryInstructions: gate.entryInstructions ?? '',
          }
        : {
            name: '',
            latitude: undefined as unknown as number,
            longitude: undefined as unknown as number,
            entryInstructions: '',
          },
    );
  }, [open, gate, reset]);

  const mutation = useMutation({
    mutationFn: (values: GateValues) =>
      isEdit
        ? updateGate(propertyId, gate.id, {
            name: values.name,
            latitude: values.latitude,
            longitude: values.longitude,
            entryInstructions: values.entryInstructions?.trim() ?? null,
          })
        : createGate(propertyId, {
            name: values.name,
            latitude: values.latitude,
            longitude: values.longitude,
            entryInstructions: values.entryInstructions?.trim() ?? null,
            deviceMode: 'personal',
            zoneId: null,
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'gates', propertyId] });
      onOpenChange(false);
      toast.success(isEdit ? 'Gate updated' : 'Gate added successfully');
    },
    onError: (err: unknown) => {
      toast.error(extractApiErrorMessage(err, 'Failed to save gate. Please try again.'));
    },
  });

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeoError('Location is not available in this browser.');
      return;
    }
    setGeoError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setValue('latitude', Number(pos.coords.latitude.toFixed(7)), { shouldValidate: true });
        setValue('longitude', Number(pos.coords.longitude.toFixed(7)), { shouldValidate: true });
        setLocating(false);
      },
      () => {
        setGeoError('Could not get your location. Check browser permissions or enter it manually.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Gate' : 'Add Gate'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="gate-name">Gate Name</Label>
            <Input id="gate-name" {...register('name')} placeholder="e.g. Main Gate" />
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Gate Location</Label>
              <button
                type="button"
                onClick={useCurrentLocation}
                disabled={locating}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
              >
                <MapPin className="h-3.5 w-3.5" />
                {locating ? 'Locating…' : 'Use my current location'}
              </button>
            </div>
            <p className="text-xs text-[var(--text-tertiary)]">
              Visitors see this on the map and use it for directions. Stand at the gate and tap the
              button, or enter coordinates manually.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Input
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  aria-label="Latitude"
                  {...register('latitude', { valueAsNumber: true })}
                />
                {errors.latitude && (
                  <p className="text-xs text-destructive">{errors.latitude.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <Input
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  aria-label="Longitude"
                  {...register('longitude', { valueAsNumber: true })}
                />
                {errors.longitude && (
                  <p className="text-xs text-destructive">{errors.longitude.message}</p>
                )}
              </div>
            </div>
            {geoError && <p className="text-xs text-destructive">{geoError}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gate-instructions">
              Entry Instructions{' '}
              <span className="text-[var(--text-tertiary)] font-normal">(optional)</span>
            </Label>
            <Input
              id="gate-instructions"
              {...register('entryInstructions')}
              placeholder="e.g. Use the visitor lane on the left"
            />
            {errors.entryInstructions && (
              <p className="text-sm text-destructive">{errors.entryInstructions.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending
                ? isEdit
                  ? 'Saving…'
                  : 'Adding…'
                : isEdit
                  ? 'Save Changes'
                  : 'Add Gate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GatesTab({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editGate, setEditGate] = useState<PropertyGate | null>(null);
  const [deleteGateTarget, setDeleteGateTarget] = useState<{ id: string; name: string } | null>(
    null,
  );

  const query = useQuery({
    queryKey: ['pm', 'gates', propertyId],
    queryFn: () => getPmGates(propertyId),
    enabled: !!propertyId,
  });

  const deleteMutation = useMutation({
    mutationFn: (gateId: string) => deleteGate(propertyId, gateId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'gates', propertyId] });
      toast.success('Gate removed');
    },
  });

  const gates = query.data ?? [];

  const formatCoords = (gate: PropertyGate) => {
    if (gate.latitude == null || gate.longitude == null) return null;
    if (gate.latitude === 0 && gate.longitude === 0) return null;
    return `${gate.latitude.toFixed(5)}, ${gate.longitude.toFixed(5)}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setAddOpen(true)}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Gate
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Gate Name</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Device Mode</TableHead>
              <TableHead>Instructions</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 2 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : gates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-[var(--text-tertiary)] py-8">
                  No gates configured
                </TableCell>
              </TableRow>
            ) : (
              gates.map((gate) => {
                const coords = formatCoords(gate);
                return (
                  <TableRow key={gate.id}>
                    <TableCell className="font-medium">{gate.name}</TableCell>
                    <TableCell className="text-sm">
                      {coords ? (
                        <span className="font-mono text-xs">{coords}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-[var(--status-amber-text)]">
                          <MapPin className="h-3 w-3" />
                          Not set
                        </span>
                      )}
                    </TableCell>
                    <TableCell>Mode {gate.deviceMode}</TableCell>
                    <TableCell className="text-sm text-[var(--text-tertiary)] truncate max-w-xs">
                      {gate.entryInstructions ?? '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditGate(gate)}
                          aria-label="Edit gate"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={deleteMutation.isPending}
                          onClick={() => setDeleteGateTarget({ id: gate.id, name: gate.name })}
                          aria-label="Delete gate"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <GateFormDialog propertyId={propertyId} open={addOpen} onOpenChange={setAddOpen} />
      <GateFormDialog
        propertyId={propertyId}
        open={!!editGate}
        onOpenChange={(open) => {
          if (!open) setEditGate(null);
        }}
        gate={editGate}
      />

      <ConfirmDialog
        open={!!deleteGateTarget}
        title="Delete Gate"
        description={`Are you sure you want to delete "${deleteGateTarget?.name ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteGateTarget) deleteMutation.mutate(deleteGateTarget.id);
          setDeleteGateTarget(null);
        }}
        onCancel={() => setDeleteGateTarget(null)}
      />
    </div>
  );
}

// ── Branding tab ──────────────────────────────────────────────────────────────

const brandingSchema = z.object({
  emailSenderName: z.string().min(1, 'Sender name is required'),
  primaryColour: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be a hex colour'),
  secondaryColour: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Must be a hex colour')
    .or(z.literal('')),
  smsSenderId: z.string().max(11, 'Max 11 characters').optional(),
});
type BrandingValues = z.infer<typeof brandingSchema>;

function BrandingTab({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const query = useQuery({
    queryKey: ['pm', 'branding', propertyId],
    queryFn: () => getBranding(propertyId),
    enabled: !!propertyId,
  });

  const mutation = useMutation({
    mutationFn: (values: BrandingValues) =>
      updateBranding(propertyId, {
        primaryColour: values.primaryColour,
        secondaryColour: values.secondaryColour ?? null,
        emailSenderName: values.emailSenderName,
        smsSenderId: values.smsSenderId ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'branding', propertyId] });
      toast.success('Branding updated');
    },
  });

  const logoMutation = useMutation({
    mutationFn: (file: File) => uploadBrandingLogo(propertyId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'branding', propertyId] });
      toast.success('Logo uploaded');
    },
    onError: () => {
      toast.error('Logo upload failed. Use a PNG or JPEG under 2 MB.');
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<BrandingValues>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {
      primaryColour: '#F4A261',
      secondaryColour: '',
      emailSenderName: '',
      smsSenderId: '',
    },
  });

  useEffect(() => {
    if (query.data) {
      reset({
        emailSenderName: query.data.emailSenderName ?? '',
        primaryColour: query.data.primaryColour ?? '#F4A261',
        secondaryColour: query.data.secondaryColour ?? '',
        smsSenderId: query.data.smsSenderId ?? '',
      });
    }
  }, [query.data, reset]);

  const colour = watch('primaryColour');
  const secondaryColour = watch('secondaryColour');

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2 MB');
      return;
    }
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast.error('Logo must be a PNG or JPEG image');
      return;
    }
    logoMutation.mutate(file);
  };

  if (query.isLoading) return <Skeleton className="h-64 rounded-lg" />;

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-6 max-w-lg">
      {/* Logo Upload */}
      <div className="space-y-2">
        <Label>Estate Logo</Label>
        <div className="flex items-center gap-4">
          {query.data?.logoUrl ? (
            <img
              src={query.data.logoUrl}
              alt="Estate logo"
              className="h-16 w-16 rounded-md border object-contain bg-white"
            />
          ) : (
            <div className="h-16 w-16 rounded-md border border-dashed flex items-center justify-center text-muted-foreground text-xs">
              No logo
            </div>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handleLogoChange}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={logoMutation.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              {logoMutation.isPending
                ? 'Uploading…'
                : query.data?.logoUrl
                  ? 'Change Logo'
                  : 'Upload Logo'}
            </Button>
            <p className="text-xs text-muted-foreground mt-1">PNG or JPEG, max 2 MB</p>
          </div>
        </div>
      </div>

      {/* Email Sender Name */}
      <div className="space-y-1.5">
        <Label htmlFor="brand-sender">Email Sender Name</Label>
        <Input id="brand-sender" {...register('emailSenderName')} placeholder="e.g. Runda Estate" />
        <p className="text-xs text-muted-foreground">
          The &ldquo;From&rdquo; name on invitation and notification emails sent to visitors and
          residents.
        </p>
        {errors.emailSenderName && (
          <p className="text-sm text-destructive">{errors.emailSenderName.message}</p>
        )}
      </div>

      {/* SMS Sender ID */}
      <div className="space-y-1.5">
        <Label htmlFor="brand-sms">SMS Sender ID</Label>
        <Input
          id="brand-sms"
          {...register('smsSenderId')}
          placeholder="e.g. RundaEst"
          maxLength={11}
        />
        <p className="text-xs text-muted-foreground">
          Up to 11 characters. Shown as the sender on SMS notifications (carrier support varies).
        </p>
        {errors.smsSenderId && (
          <p className="text-sm text-destructive">{errors.smsSenderId.message}</p>
        )}
      </div>

      {/* Primary Colour */}
      <div className="space-y-1.5">
        <Label htmlFor="brand-colour">Primary Colour</Label>
        <div className="flex items-center gap-3">
          <Input
            id="brand-colour"
            type="color"
            className="h-10 w-16 cursor-pointer p-1"
            {...register('primaryColour')}
          />
          <Input
            value={colour}
            onChange={() => {}}
            readOnly
            className="w-32 font-mono text-sm"
            aria-label="Hex colour value"
          />
          <div
            className="h-10 w-10 rounded border"
            style={{ backgroundColor: colour }}
            aria-hidden
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Used on buttons, links, and highlights across visitor-facing pages and emails.
        </p>
        {errors.primaryColour && (
          <p className="text-sm text-destructive">{errors.primaryColour.message}</p>
        )}
      </div>

      {/* Secondary Colour */}
      <div className="space-y-1.5">
        <Label htmlFor="brand-colour-secondary">Secondary Colour</Label>
        <div className="flex items-center gap-3">
          <Input
            id="brand-colour-secondary"
            type="color"
            className="h-10 w-16 cursor-pointer p-1"
            {...register('secondaryColour')}
          />
          <Input
            value={secondaryColour}
            onChange={() => {}}
            readOnly
            className="w-32 font-mono text-sm"
            aria-label="Secondary hex colour value"
          />
          {secondaryColour && (
            <div
              className="h-10 w-10 rounded border"
              style={{ backgroundColor: secondaryColour }}
              aria-hidden
            />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Optional. Used for backgrounds and borders on branded pages.
        </p>
        {errors.secondaryColour && (
          <p className="text-sm text-destructive">{errors.secondaryColour.message}</p>
        )}
      </div>

      {mutation.isError && (
        <p className="text-sm text-destructive">Save failed. Please try again.</p>
      )}
      <Button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? 'Saving…' : 'Save Branding'}
      </Button>
    </form>
  );
}

// ── Units tab ─────────────────────────────────────────────────────────────────

const optionalCoord = (min: number, max: number, message: string) =>
  z
    .number()
    .or(z.nan())
    .optional()
    .refine((v) => v === undefined || Number.isNaN(v) || (v >= min && v <= max), { message });

const unitSchema = z.object({
  unitReference: z.string().min(1, 'Unit identifier is required'),
  zoneId: z.string().min(1, 'Zone is required'),
  unitType: z.enum(['residential', 'enterprise']),
  floor: z.string().optional(),
  isActive: z.boolean().optional(),
  isSelfManaged: z.boolean().optional(),
  destinationLatitude: optionalCoord(-90, 90, 'Latitude must be between -90 and 90'),
  destinationLongitude: optionalCoord(-180, 180, 'Longitude must be between -180 and 180'),
});
type UnitFormValues = z.infer<typeof unitSchema>;

function UnitFormDialog({
  open,
  onOpenChange,
  propertyId,
  zones,
  editUnit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  zones: Array<{ id: string; name: string }>;
  editUnit?: Unit | null;
}) {
  const qc = useQueryClient();
  const isEdit = !!editUnit;
  const [unitGeoError, setUnitGeoError] = useState<string | null>(null);
  const [unitLocating, setUnitLocating] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UnitFormValues>({
    resolver: zodResolver(unitSchema),
    defaultValues: {
      unitReference: editUnit?.unitNumber ?? '',
      zoneId: editUnit?.zoneId ?? zones[0]?.id ?? '',
      unitType: (editUnit?.type as 'residential' | 'enterprise') ?? 'residential',
      floor: editUnit?.floor ?? '',
      isActive: editUnit?.isActive !== false,
      isSelfManaged: editUnit?.isSelfManaged === true,
      destinationLatitude: editUnit?.destinationLatitude ?? undefined,
      destinationLongitude: editUnit?.destinationLongitude ?? undefined,
    },
  });

  useEffect(() => {
    if (open) {
      setUnitGeoError(null);
      reset({
        unitReference: editUnit?.unitNumber ?? '',
        zoneId: editUnit?.zoneId ?? zones[0]?.id ?? '',
        unitType: (editUnit?.type as 'residential' | 'enterprise') ?? 'residential',
        floor: editUnit?.floor ?? '',
        isActive: editUnit?.isActive !== false,
        isSelfManaged: editUnit?.isSelfManaged === true,
        destinationLatitude: editUnit?.destinationLatitude ?? undefined,
        destinationLongitude: editUnit?.destinationLongitude ?? undefined,
      });
    }
  }, [open, editUnit, zones, reset]);

  const createMutation = useMutation({
    mutationFn: (values: UnitFormValues) =>
      createUnit(propertyId, {
        unitReference: values.unitReference,
        zoneId: values.zoneId,
        unitType: values.unitType,
        ...(values.floor ? { floor: values.floor } : {}),
        ...(typeof values.destinationLatitude === 'number' &&
        !Number.isNaN(values.destinationLatitude)
          ? { destinationLatitude: values.destinationLatitude }
          : {}),
        ...(typeof values.destinationLongitude === 'number' &&
        !Number.isNaN(values.destinationLongitude)
          ? { destinationLongitude: values.destinationLongitude }
          : {}),
        ...(values.isSelfManaged ? { isSelfManaged: true } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'units', propertyId] });
      onOpenChange(false);
      reset();
      toast.success('Unit added');
    },
    onError: () => toast.error('Failed to add unit. It may already exist.'),
  });

  const updateMutation = useMutation({
    mutationFn: (values: UnitFormValues) => {
      if (!editUnit) throw new Error('No unit selected for editing');
      return updateUnit(propertyId, editUnit.id, {
        unitReference: values.unitReference,
        zoneId: values.zoneId,
        unitType: values.unitType,
        floor: values.floor ?? undefined,
        ...(values.isActive !== undefined ? { isActive: values.isActive } : {}),
        ...(values.isSelfManaged !== undefined ? { isSelfManaged: values.isSelfManaged } : {}),
        destinationLatitude:
          typeof values.destinationLatitude === 'number' &&
          !Number.isNaN(values.destinationLatitude)
            ? values.destinationLatitude
            : null,
        destinationLongitude:
          typeof values.destinationLongitude === 'number' &&
          !Number.isNaN(values.destinationLongitude)
            ? values.destinationLongitude
            : null,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'units', propertyId] });
      onOpenChange(false);
      toast.success('Unit updated');
    },
    onError: () => toast.error('Failed to update unit'),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const useDestinationLocation = () => {
    if (!navigator.geolocation) {
      setUnitGeoError('Location is not available in this browser.');
      return;
    }
    setUnitGeoError(null);
    setUnitLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setValue('destinationLatitude', Number(pos.coords.latitude.toFixed(7)), {
          shouldValidate: true,
        });
        setValue('destinationLongitude', Number(pos.coords.longitude.toFixed(7)), {
          shouldValidate: true,
        });
        setUnitLocating(false);
      },
      () => {
        setUnitGeoError(
          'Could not get your location. Check browser permissions or enter it manually.',
        );
        setUnitLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const onSubmit = (values: UnitFormValues) => {
    const lat =
      typeof values.destinationLatitude === 'number' && !Number.isNaN(values.destinationLatitude)
        ? values.destinationLatitude
        : undefined;
    const lng =
      typeof values.destinationLongitude === 'number' && !Number.isNaN(values.destinationLongitude)
        ? values.destinationLongitude
        : undefined;
    const normalised = { ...values, destinationLatitude: lat, destinationLongitude: lng };
    if (isEdit) updateMutation.mutate(normalised);
    else createMutation.mutate(normalised);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          onOpenChange(false);
          reset();
        }
      }}
    >
      <DialogContent aria-describedby={undefined} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Unit' : 'Add Unit'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="unit-ref">Unit Identifier</Label>
            <Input
              id="unit-ref"
              {...register('unitReference')}
              placeholder="e.g. House 1, A101, Suite 5"
            />
            {errors.unitReference && (
              <p className="text-sm text-destructive">{errors.unitReference.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              This is what residents enter during self-registration (case-insensitive).
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="unit-type">Type</Label>
            <Select
              defaultValue={editUnit?.type ?? 'residential'}
              onValueChange={(v) => setValue('unitType', v as 'residential' | 'enterprise')}
            >
              <SelectTrigger id="unit-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="unit-zone">Zone</Label>
            {zones.length === 1 ? (
              <Input
                id="unit-zone"
                readOnly
                value={zones[0]?.name ?? ''}
                className="bg-muted text-muted-foreground"
              />
            ) : (
              <Select
                defaultValue={editUnit?.zoneId ?? zones[0]?.id ?? ''}
                onValueChange={(v) => setValue('zoneId', v)}
              >
                <SelectTrigger id="unit-zone">
                  <SelectValue placeholder="Select zone" />
                </SelectTrigger>
                <SelectContent>
                  {zones.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      {z.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="unit-floor">Floor (optional)</Label>
            <Input id="unit-floor" {...register('floor')} placeholder="e.g. Ground, 1st, 2nd" />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Destination Location (optional)</Label>
              <button
                type="button"
                onClick={useDestinationLocation}
                disabled={unitLocating}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
              >
                <MapPin className="h-3.5 w-3.5" />
                {unitLocating ? 'Locating…' : 'Use my current location'}
              </button>
            </div>
            <p className="text-xs text-[var(--text-tertiary)]">
              Where visitors should head once inside the estate. Stand at the unit and tap the
              button, or enter coordinates manually. Leave blank if not needed.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Input
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  aria-label="Destination latitude"
                  {...register('destinationLatitude', { valueAsNumber: true })}
                />
                {errors.destinationLatitude && (
                  <p className="text-xs text-destructive">{errors.destinationLatitude.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <Input
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  aria-label="Destination longitude"
                  {...register('destinationLongitude', { valueAsNumber: true })}
                />
                {errors.destinationLongitude && (
                  <p className="text-xs text-destructive">{errors.destinationLongitude.message}</p>
                )}
              </div>
            </div>
            {unitGeoError && <p className="text-xs text-destructive">{unitGeoError}</p>}
          </div>

          {isEdit && (
            <div className="flex items-center gap-3">
              <Label htmlFor="unit-active">Active</Label>
              <input
                id="unit-active"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                {...register('isActive')}
              />
              <span className="text-xs text-muted-foreground">
                Inactive units are hidden from resident self-registration.
              </span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Label htmlFor="unit-self-managed">Self-managed (no approval)</Label>
            <input
              id="unit-self-managed"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              {...register('isSelfManaged')}
            />
            <span className="text-xs text-muted-foreground">
              Resident is not on the system. Walk-ins to this unit are logged and admitted by the
              guard without an approval request.
            </span>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Unit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UnitsTab({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Unit | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const query = useQuery({
    queryKey: ['pm', 'units', propertyId],
    queryFn: () => getUnits(propertyId),
    enabled: !!propertyId,
  });

  const zonesQuery = useQuery({
    queryKey: ['pm', 'units-zones', propertyId],
    queryFn: () => getZonesForUnits(propertyId),
    enabled: !!propertyId,
  });

  const deleteMutation = useMutation({
    mutationFn: (unitId: string) => deleteUnit(propertyId, unitId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'units', propertyId] });
      toast.success('Unit removed');
    },
    onError: (err) => {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast.error(msg ?? 'Failed to remove unit');
    },
  });

  const units: Unit[] = query.data?.data ?? [];
  const zones = zonesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setAddOpen(true)} disabled={zones.length === 0}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Unit
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Unit No.</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Floor</TableHead>
              <TableHead>Zone</TableHead>
              <TableHead>Residents</TableHead>
              <TableHead>Destination</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : units.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[var(--text-tertiary)] py-8">
                  No units configured — add units so residents can self-register.
                </TableCell>
              </TableRow>
            ) : (
              units.map((u) => (
                <TableRow key={u.id} className={u.isActive === false ? 'opacity-60' : ''}>
                  <TableCell className="font-medium">{u.unitNumber}</TableCell>
                  <TableCell className="capitalize">{u.type}</TableCell>
                  <TableCell>{u.floor ?? '—'}</TableCell>
                  <TableCell>{u.zoneName ?? '—'}</TableCell>
                  <TableCell className="text-sm">{u.residentCount ?? 0}</TableCell>
                  <TableCell className="text-sm">
                    {typeof u.destinationLatitude === 'number' &&
                    typeof u.destinationLongitude === 'number' ? (
                      <span className="inline-flex items-center gap-1 text-[var(--status-green-text)]">
                        <MapPin className="h-3.5 w-3.5" />
                        Set
                      </span>
                    ) : (
                      <span className="text-[var(--text-tertiary)]">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        u.isActive === false
                          ? 'text-xs px-2 py-0.5 rounded-full bg-[var(--status-red)]/10 text-[var(--status-red-text)]'
                          : 'text-xs px-2 py-0.5 rounded-full bg-[var(--status-green)]/10 text-[var(--status-green-text)]'
                      }
                    >
                      {u.isActive === false ? 'Inactive' : 'Active'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditTarget(u)}
                        aria-label="Edit unit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={deleteMutation.isPending}
                        onClick={() => setDeleteTarget({ id: u.id, name: u.unitNumber })}
                        aria-label="Delete unit"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <UnitFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        propertyId={propertyId}
        zones={zones}
      />

      <UnitFormDialog
        open={!!editTarget}
        onOpenChange={(v) => {
          if (!v) setEditTarget(null);
        }}
        propertyId={propertyId}
        zones={zones}
        editUnit={editTarget}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Unit"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? ''}"? Residents assigned to this unit will lose their unit association.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ── Page root ─────────────────────────────────────────────────────────────────

export function PropertySetupPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';

  const modulesQuery = useQuery({
    queryKey: ['pm', 'modules', propertyId],
    queryFn: () => getPropertyModules(propertyId),
    enabled: !!propertyId,
  });

  const hasWhiteLabel =
    modulesQuery.data?.some((m) => m.moduleKey === 'WHITE_LABEL' && m.active) ?? false;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl">Property Setup</h1>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="gates">Gates</TabsTrigger>
          {hasWhiteLabel && <TabsTrigger value="branding">Branding</TabsTrigger>}
          <TabsTrigger value="units">Units</TabsTrigger>
        </TabsList>
        <TabsContent value="details" className="mt-4">
          <DetailsTab propertyId={propertyId} />
        </TabsContent>
        <TabsContent value="gates" className="mt-4">
          <GatesTab propertyId={propertyId} />
        </TabsContent>
        {hasWhiteLabel && (
          <TabsContent value="branding" className="mt-4">
            <BrandingTab propertyId={propertyId} />
          </TabsContent>
        )}
        <TabsContent value="units" className="mt-4">
          <UnitsTab propertyId={propertyId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
